/**
 * One checkout surface per PaymentFailureCode.
 *
 * Screens must not collapse every processor failure into `payment.failed`.
 * This table is the contract: a second PSP maps into these codes and reuses
 * the same notice.
 */
import { classifyPaymentFailure, type ClassifiedPaymentFailure } from './classify-failure'
import type { PaymentFailureCode } from './codes'

export type PaymentOutcomeTone = 'error' | 'warning'

export type PaymentOutcomePresentation = {
  code: PaymentFailureCode
  titleKey: string
  bodyKey: string
  tone: PaymentOutcomeTone
  testID: string
  retryable: boolean
}

const PRESENTATION: Record<PaymentFailureCode, Omit<PaymentOutcomePresentation, 'code'>> = {
  DECLINED: {
    titleKey: 'payment.declined_title',
    bodyKey: 'payment.declined_desc',
    tone: 'error',
    testID: 'checkout.outcome.declined',
    retryable: true,
  },
  VALIDATION: {
    titleKey: 'payment.validation_title',
    bodyKey: 'payment.validation',
    tone: 'error',
    testID: 'checkout.outcome.validation',
    retryable: true,
  },
  APP_NOT_LIVE: {
    titleKey: 'payment.app_not_live_title',
    bodyKey: 'payment.app_not_live',
    tone: 'warning',
    testID: 'checkout.outcome.app-not-live',
    retryable: false,
  },
  CHALLENGE_ABANDONED: {
    titleKey: 'payment.challenge_abandoned_title',
    bodyKey: 'payment.challenge_abandoned',
    tone: 'warning',
    testID: 'checkout.outcome.challenge-abandoned',
    retryable: true,
  },
  START_IN_PAST: {
    titleKey: 'payment.start_in_past_title',
    bodyKey: 'checkout.start_in_the_past',
    tone: 'warning',
    testID: 'checkout.outcome.start-in-past',
    retryable: false,
  },
  BOOKING_TOO_SOON: {
    titleKey: 'payment.booking_too_soon_title',
    bodyKey: 'payment.booking_too_soon',
    tone: 'warning',
    testID: 'checkout.outcome.booking-too-soon',
    retryable: false,
  },
  CREATE_FAILED: {
    titleKey: 'payment.create_failed_title',
    bodyKey: 'payment.could_not_start',
    tone: 'error',
    testID: 'checkout.outcome.create-failed',
    retryable: true,
  },
  RENTAL_NO_LONGER_ACTIVE: {
    titleKey: 'payment.refunded_title',
    bodyKey: 'payment.rental_no_longer_active',
    tone: 'warning',
    testID: 'checkout.outcome.rental-gone',
    retryable: false,
  },
  RENTAL_TOTAL_MISMATCH: {
    titleKey: 'payment.total_mismatch_title',
    bodyKey: 'payment.total_mismatch',
    tone: 'warning',
    testID: 'checkout.outcome.total-mismatch',
    retryable: false,
  },
  COMPLIANCE_INCOMPLETE: {
    titleKey: 'payment.booking_blocked_title',
    bodyKey: 'payment.booking_blocked_unknown',
    tone: 'warning',
    testID: 'checkout.outcome.compliance',
    retryable: false,
  },
  SAVED_CARD_DISABLED: {
    titleKey: 'payment.saved_card_title',
    bodyKey: 'payment.saved_card_unavailable',
    tone: 'warning',
    testID: 'checkout.outcome.saved-card',
    retryable: true,
  },
  HOSTED_CHECKOUT_FAILED: {
    titleKey: 'payment.hosted_checkout_title',
    bodyKey: 'payment.hosted_checkout_could_not_start',
    tone: 'error',
    testID: 'checkout.outcome.hosted-failed',
    retryable: true,
  },
  HOSTED_CHECKOUT_DISABLED: {
    titleKey: 'payment.hosted_checkout_title',
    bodyKey: 'payment.hosted_checkout_could_not_start',
    tone: 'warning',
    testID: 'checkout.outcome.hosted-disabled',
    retryable: false,
  },
  UNKNOWN: {
    titleKey: 'payment.failed',
    bodyKey: 'payment.failed_desc',
    tone: 'error',
    testID: 'checkout.outcome.unknown',
    retryable: true,
  },
}

export function paymentOutcomePresentation(code: PaymentFailureCode): PaymentOutcomePresentation {
  return { code, ...PRESENTATION[code] }
}

export function classifyUnknownPaymentFailure(err: unknown): ClassifiedPaymentFailure {
  const record =
    err && typeof err === 'object'
      ? (err as { paymentCode?: string; code?: string; message?: string })
      : {}
  return classifyPaymentFailure({
    code: record.paymentCode ?? record.code,
    message: record.message ?? (err instanceof Error ? err.message : null),
  })
}
